import { Directive, ElementRef, inject, input } from "@angular/core";

/**
 * This directive will automatically add a class to the element before it receives focus (through the focus method call), for the duration of the call.
 * This is useful for elements that are normally hidden and thus cannot get focus, such as hover-only icons.
 *
 * Note that the directive helps only for explicit focus calls, not for browser behaviour (tabbing). The directive also doesn't make the element itself focusable (it won't set tabindex).
 *
 * The class name must be provided.
 *
 * @example
 * ```css
 * .lg-flexlayout-panel__icon--allowFocus {
 *    transition: visibility 0ms;
 *    visibility: visible;
 * }
 * ```
 * ```html
 * <lg-icon
 *    class="lg-flexlayout-panel__icon lg-flexlayout-panel__icon--duplicate lg-flexlayout-panel__icon--hover-only"
 *    icon="icon-duplicate"
 *    [clickable]="true"
 *    lgClassBeforeFocus="lg-flexlayout-panel__icon--allowFocus"
 *    tabindex="-1"
 * />
 * ```
 */
@Directive({
    selector: "[lgClassBeforeFocus]"
})
export class LgClassBeforeFocusDirective {
    private _element = inject(ElementRef<HTMLElement>);

    className = input.required({ alias: "lgClassBeforeFocus" });

    constructor() {
        const originalHandler = this._element.nativeElement.focus;
        this._element.nativeElement.focus = () => {
            const name = this.className();
            if (name) {
                this._element.nativeElement.classList.add(name);
            }
            originalHandler.call(this._element.nativeElement);
            if (name) {
                this._element.nativeElement.classList.remove(name);
            }
        };
    }
}
